import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BreadcrumbComponent } from '../../Components/BreadCrumb';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useBuildingsData } from '../../Helpers/BuildingSummariesContext';
import { useProject } from '../../hooks/projects/useProject';
import { ProjectPreviewPane } from './ProjectPreviewPane';
import './ProjectPage.css';

/**
 * ProjectPage - one project at /building/:id/projects/:projectKey. The board
 * links here; the page is the former preview pane given the full width.
 */
const ProjectPage: React.FC = () => {
    const { id: buildingKey = '', projectKey = '' } = useParams<{ id: string; projectKey: string }>();
    const navigate = useNavigate();
    const { buildings } = useBuildingsData();

    const building = useMemo(() => buildings?.find(b => b.key === buildingKey), [buildings, buildingKey]);

    // Only the title is needed here; the pane fetches the project itself.
    const { project } = useProject({ buildingKey, projectKey });

    useDocumentTitle(project?.title ? `${project.title} - Projekt` : 'Projekt');

    const coverPhotoPath = useMemo(() => {
        if (!building) return undefined;
        const currentKey = building.currentCoverPhotoKey;
        return (currentKey && building.coverPhotos?.[currentKey]?.storagePath)
            || building.coverPhoto?.storagePath;
    }, [building]);

    const breadcrumbItems = [
        { text: building?.name ?? 'Byggnad', link: `/building/${buildingKey}` },
        { text: 'Projekt', link: `/building/${buildingKey}/projects` },
        { text: project?.title ?? 'Projekt' }
    ];

    return (
        <div className="project-page">
            <div style={{ flexShrink: 0 }}>
                <BreadcrumbComponent items={breadcrumbItems} />
            </div>
            <div className="project-page__content">
                <ProjectPreviewPane
                    buildingKey={buildingKey}
                    projectKey={projectKey}
                    buildingName={building?.name}
                    buildingType={building?.buildingType}
                    buildingCity={building?.buildingInfo?.city}
                    buildingMunicipality={building?.buildingInfo?.municipality}
                    buildingGroupTags={building?.groupTags}
                    buildingCoverPhotoPath={coverPhotoPath}
                    onProjectDeleted={() => navigate(`/building/${buildingKey}/projects`)}
                />
            </div>
        </div>
    );
};

export default ProjectPage;
